import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  height: 100%;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  background-color: transparent;
  overflow-y: auto;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 500;
  margin-bottom: 1rem;
  color: #333;
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  margin: 1rem 0;
  padding-left: 2rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 0.5rem;
`;

const CodeBlock = styled.pre`
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-x: auto;
  margin: 1rem 0;
  border: 1px solid #e0e0e0;

  code {
    display: block;
    white-space: pre;
  }
`;

const Note = styled.div`
  background-color: #fff8e6;
  border-left: 4px solid #ffd700;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 0 4px 4px 0;
`;

const Warning = styled.div`
  background-color: #fff2f0;
  border-left: 4px solid #ff4d4f;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 0 4px 4px 0;
`;

const ImplementationGuide = () => {
    return (
        <Container>
            <Title>Implementation Guide</Title>

            <Section>
                <SectionTitle>Quick Start</SectionTitle>
                <Description>
                    Follow these simple steps to add the radio player to your website:
                </Description>
                <List>
                    <ListItem>Copy the embed code from the builder</ListItem>
                    <ListItem>Paste it into your HTML file where you want the player to appear</ListItem>
                    <ListItem>Make sure you have an internet connection to load the required resources</ListItem>
                </List>
            </Section>

            <Section>
                <SectionTitle>Requirements</SectionTitle>
                <Description>
                    Before implementing the player, ensure your website meets these requirements:
                </Description>
                <List>
                    <ListItem>Modern web browser support (Chrome, Firefox, Safari, Edge)</ListItem>
                    <ListItem>Internet connection to load Font Awesome icons</ListItem>
                    <ListItem>HTTPS connection for secure audio streaming</ListItem>
                    <ListItem>JavaScript enabled in the browser</ListItem>
                </List>
            </Section>

            <Section>
                <SectionTitle>Implementation Examples</SectionTitle>

                <Description>Basic HTML page implementation:</Description>
                <CodeBlock>
                    <code>{`<!DOCTYPE html>
<html>
<head>
    <title>My Radio Station</title>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body>
    <!-- Paste your embed code here -->
    [Your Radio Player Embed Code]
</body>
</html>`}</code>
                </CodeBlock>

                <Description>WordPress implementation:</Description>
                <CodeBlock>
                    <code>{`<!-- In your WordPress page/post editor -->
<!-- Switch to HTML/Code editor mode -->
[Your Radio Player Embed Code]

<!-- Or in your theme's template file (e.g., page.php) -->
<?php
get_header();
?>
<div class="content-area">
    <!-- Your Radio Player Embed Code -->
</div>
<?php
get_footer();
?>`}</code>
                </CodeBlock>
            </Section>

            <Section>
                <SectionTitle>Troubleshooting</SectionTitle>

                <Warning>
                    Common issues and their solutions:
                    <List>
                        <ListItem>
                            <strong>Player not loading:</strong> Check if all required resources are accessible and JavaScript is enabled
                        </ListItem>
                        <ListItem>
                            <strong>Audio not playing:</strong> Verify that the stream URL is correct and accessible
                        </ListItem>
                        <ListItem>
                            <strong>Icons not showing:</strong> Ensure Font Awesome CDN is not blocked by your network
                        </ListItem>
                        <ListItem>
                            <strong>Metadata not updating:</strong> Check if the API URL is correct and accessible
                        </ListItem>
                    </List>
                </Warning>
            </Section>

            <Section>
                <SectionTitle>Best Practices</SectionTitle>
                <List>
                    <ListItem>Place the player in a visible and easily accessible location</ListItem>
                    <ListItem>Ensure the container width is at least 300px for optimal display</ListItem>
                    <ListItem>Test the player on different devices and screen sizes</ListItem>
                    <ListItem>Keep your stream and API URLs up to date</ListItem>
                </List>
            </Section>

            <Section>
                <SectionTitle>Advanced Usage</SectionTitle>
                <Description>
                    The player exposes a global object that you can use to control it programmatically:
                </Description>
                <CodeBlock>
                    <code>{`// Play/pause the stream
window.radioPlayer.togglePlay();

// Set volume (0-1)
window.radioPlayer.setVolume(0.5);

// Toggle mute
window.radioPlayer.toggleMute();

// Force metadata refresh
window.radioPlayer.fetchMetadata(true);`}</code>
                </CodeBlock>

                <Note>
                    Note: Make sure the player is fully initialized before calling these methods. You can wrap your code in the
                    DOMContentLoaded event listener to ensure this.
                </Note>
            </Section>

            <Section>
                <SectionTitle>Support</SectionTitle>
                <Description>
                    If you encounter any issues or need assistance, please check the following resources:
                </Description>
                <List>
                    <ListItem>Review the troubleshooting section above</ListItem>
                    <ListItem>Check your browser's console for error messages</ListItem>
                    <ListItem>Verify your stream and API endpoints are working</ListItem>
                    <ListItem>Contact our support team if issues persist</ListItem>
                </List>
            </Section>
        </Container>
    );
};

export default ImplementationGuide; 